import { ArrowRight, Calendar, CheckCircle, ChevronRight, Search } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { isPast } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import kebabCase from 'lodash/kebabCase';
import shuffle from 'lodash/shuffle';
import styled, { css } from 'styled-components';

import { GetHomePageQuery as GetHomePage, SportType } from '__generated__/graphql';

import { Avatar } from '../Common/Avatar';
import { Circles } from './Circles';
import { Container, NavItem, Section } from '../Common/Layout';
import { EventCard } from './EventCard';
import { SortOption } from './CalendarPage';
import { assetUrl, encodeUrl, guessCountry, resizedImageUrl, ucFirst } from '../../common/helpers';
import { popularSportTypes } from '../../backend/Events/useGroupedSportTypes';
import { useNavigation } from '../Common/useNavigation';
import AtletaLogo from '../../common/Common/AtletaLogo';
import DateRange from '../../common/Common/DateRange';
import GetHomePageQuery from './GetHomePageQuery';
import UI from '../../common/UI';
import useDocumentTitle from '../../common/useDocumentTitle';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useQuery from '../../api/useQuery';

const HomePage = () => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { locale } = useLocale();
  const device = useMediaDevice();

  useDocumentTitle(['Atleta', t('website.document_title')]);

  const country = useMemo(() => {
    const detectedCountry = guessCountry();

    if (['NL', 'DE', 'BE', 'FR'].includes(detectedCountry)) {
      // Only show 'Upcoming in country' section if we are sure we have enough events for that country.
      return detectedCountry;
    }

    return null;
  }, []);

  const { data, loading } = useQuery(GetHomePageQuery, {
    variables: {
      countries: country ? [country] : null,
    },
  });

  const session = data?.session;
  const user = session?.user;
  const upcomingEvents = data?.upcomingEvents.data;
  const trendingEvents = data?.trendingEvents.data;

  return (
    <UI.Div>
      <Header>
        <HeaderBackground>
          {/* eslint-disable jsx-a11y/control-has-associated-label */}
          {device.isPortrait && (
            <video loop autoPlay playsInline muted>
              <source src={assetUrl('images/website/header-square.webm')} type="video/webm" />
              <source src={assetUrl('images/website/header-square.mp4')} type="video/mp4" />
            </video>
          )}
          {!device.isPortrait && (
            <video loop autoPlay playsInline muted>
              <source src={assetUrl('images/website/header.webm')} type="video/webm" />
              <source src={assetUrl('images/website/header.mp4')} type="video/mp4" />
            </video>
          )}
        </HeaderBackground>

        <UI.Div>
          <Container size="sm" style={{ transform: `translateY(${data ? 0 : 50}px)`, opacity: data ? 1 : 0, transition: 'all 0.2s ease-in-out' }}>
            <UI.Div>
              <UI.Div sc={{ mb: 1 }}>
                <AtletaLogo symbolOnly height={36} />
              </UI.Div>
              <UI.H1 sc={{ mb: 4, color: 'white' }}>
                {t('website.home_title')}
              </UI.H1>
              <UI.LinkButton
                to={r('Calendar')}
                sc={{ brand: 'primary.600', size: 'lg' }}
              >
                <UI.Icon>
                  <Search />
                </UI.Icon>
                {' '}
                {t('website.discover_events')}
              </UI.LinkButton>
            </UI.Div>
          </Container>

          <Circles />

          <UI.Div style={{ transform: `translateY(${data ? 0 : 50}px)`, transition: 'all 0.4s ease-in-out' }}>
            {data && (
              <UI.FadeIn>
                {user && (
                  <Container>
                    <UI.FlexContainer sc={{ justifyContent: 'center' }}>
                      <UI.Link to={r('Portal')}>
                        <Avatar
                          background="secondary.700"
                          sportType={user?.sport_type}
                        />
                      </UI.Link>
                    </UI.FlexContainer>

                    <UI.Div sc={{ textAlign: 'center' }}>
                      <UI.H3 sc={{ fontSize: 4, color: 'white', mb: 1 }}>
                        {t('welcome_title', { name: user.first_name })}
                      </UI.H3>
                      <UI.Paragraph sc={{ color: 'secondary.100', mb: 0 }}>
                        {user.registration_summaries.length > 0
                          ? t('website.welcome_message_user', { count: user.registration_summaries.length })
                          : (
                            <Trans i18nKey="find_next_event_user_message">
                              <UI.Link to={r('Calendar')} sc={{ strong: true }} />
                            </Trans>
                          )}
                      </UI.Paragraph>
                    </UI.Div>

                    {user.registration_summaries.length > 0 && (
                      <Section>
                        {user.registration_summaries.map((upcomingEvent) => (
                          <RegistrationSummaryRow summary={upcomingEvent} key={upcomingEvent.id} />
                        ))}
                      </Section>
                    )}
                  </Container>
                )}

                {!user && (
                  <WelcomeGuestTitle />
                )}
              </UI.FadeIn>
            )}
          </UI.Div>
        </UI.Div>
      </Header>

      <Container size="lg">
        <UI.Div>
          <UI.H2 sc={{ fontWeight: 700, mb: 1 }}>
            {t('website.find_your_event')}
          </UI.H2>
          <UI.Paragraph sc={{ big: true, mb: 0, color: 'gray.800' }} style={{ maxWidth: 800, textWrap: 'balance' }}>
            {t('website.find_your_event_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.GridContainer sc={{ columns: ['1fr 1fr', '1fr 1fr 1fr', '1fr 1fr 1fr 1fr 1fr'] }}>
          {popularSportTypes.map((sportType) => (
            <UI.LinkBlock
              to={r('Calendar', { sportType: kebabCase(sportType) })}
              title={t(`common:sport_types.${sportType}`)}
              key={sportType}
            >
              <UI.Div sc={{ padding: 0, mb: 3 }}>
                <img
                  src={assetUrl(`images/event-category-headers/${sportType}.webp`)}
                  alt={t(`common:sport_types.${sportType}`)}
                  style={{
                    display: 'block', aspectRatio: '5 / 3', objectFit: 'cover', borderRadius: 16,
                  }}
                />
              </UI.Div>
              <UI.H3>
                {t(`common:sport_types.${sportType}`)}
                {' '}
                <UI.Icon>
                  <ChevronRight />
                </UI.Icon>
              </UI.H3>
            </UI.LinkBlock>
          ))}
          {!device.isMedium && (
            <UI.LinkBlock
              to={r('Calendar')}
              title={t('website.all_categories')}
            >
              <UI.Div sc={{ padding: 0, mb: 3 }}>
                <img
                  src={assetUrl('images/event-category-headers/other.webp')}
                  alt={t('website.all_categories')}
                  style={{
                    display: 'block', aspectRatio: '5 / 3', objectFit: 'cover', borderRadius: 16,
                  }}
                />
              </UI.Div>
              <UI.H3>
                {t('website.all_categories')}
                {' '}
                <UI.Icon>
                  <ChevronRight />
                </UI.Icon>
              </UI.H3>
            </UI.LinkBlock>
          )}
        </UI.GridContainer>
      </Container>

      <UI.HR sc={{ my: 0 }} />

      <UI.Div>
        <Container size="lg">
          <UI.GridContainer>
            <UI.Div>
              <UI.H2 sc={{ fontWeight: 700, mb: 1 }}>
                {t('website.trending_events_title')}
              </UI.H2>

              <UI.Paragraph sc={{ big: true, mb: 0, color: 'gray.800' }} style={{ maxWidth: 800, textWrap: 'balance' }}>
                {t('website.trending_events_description')}
              </UI.Paragraph>
            </UI.Div>

            {!trendingEvents && (
              <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr', '1fr 1fr 1fr'], loading }}>
                <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />
                {!device.isSmall && <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />}
                {device.isLarge && <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />}
                {device.isLarge && <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />}
                {device.isLarge && <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />}
                {device.isLarge && <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />}
              </UI.GridContainer>
            )}

            {trendingEvents && (
              <>
                {!device.isSmall && (
                  <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr', '1fr 1fr 1fr'] }}>
                    {trendingEvents.map((event) => (
                      <EventCard event={event} key={event.id} />
                    ))}
                  </UI.GridContainer>
                )}
                {device.isSmall && (
                  <EventSlider>
                    {trendingEvents.map((event) => (
                      <EventCard event={event} key={event.id} />
                    ))}
                    <UI.NavCard
                      to={r('Calendar')}
                      sc={{ placeholder: true, big: true, flex: { justifyContent: 'center', alignItems: 'center' } }}
                      title={t('website.view_all')}
                    >
                      <UI.Div>
                        <UI.FlexContainer sc={{ justifyContent: 'center', mb: 2 }}>
                          <UI.Icon sc={{ size: 2 }}>
                            <Calendar strokeWidth={1.125} />
                          </UI.Icon>
                        </UI.FlexContainer>
                        <UI.Div>
                          {t('website.view_all')}
                          {' '}
                          <UI.Icon>
                            <ArrowRight />
                          </UI.Icon>
                        </UI.Div>
                      </UI.Div>
                    </UI.NavCard>
                  </EventSlider>
                )}
              </>
            )}
            <UI.Div sc={{ textAlign: ['left', 'center'], big: true }}>
              <UI.Link
                to={`${r('Calendar')}?${encodeUrl({ sort: SortOption.Trending })}`}
                sc={{ secondary: true, fontWeight: 500 }}
              >
                {t('website.view_trending_events')}
                {' '}
                <UI.Icon>
                  <ChevronRight />
                </UI.Icon>
              </UI.Link>
            </UI.Div>
          </UI.GridContainer>

          <UI.GridContainer>
            <UI.Div>
              <UI.H2 sc={{ fontWeight: 700, mb: 1 }}>
                {country
                  ? t('website.upcoming_in_country_title', { country: t(`common:countries.${country}`) })
                  : t('website.upcoming_events_title')}
              </UI.H2>

              <UI.Paragraph sc={{ big: true, mb: 0, color: 'gray.800' }} style={{ maxWidth: 800, textWrap: 'balance' }}>
                {t('website.upcoming_events_description')}
              </UI.Paragraph>
            </UI.Div>

            {!upcomingEvents && (
              <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr', '1fr 1fr'], loading }}>
                <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />
                {!device.isSmall && <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />}
                {device.isLarge && <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />}
                {device.isLarge && <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />}
                {device.isLarge && <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />}
                {device.isLarge && <UI.Card sc={{ placeholder: true }} style={{ height: 230 }} />}
              </UI.GridContainer>
            )}

            {upcomingEvents && (
              <>
                {!device.isSmall && (
                  <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr', '1fr 1fr 1fr'] }}>
                    {upcomingEvents.map((event) => (
                      <EventCard event={event} key={event.id} />
                    ))}
                  </UI.GridContainer>
                )}
                {device.isSmall && (
                  <EventSlider>
                    {upcomingEvents.map((event) => (
                      <EventCard event={event} key={event.id} />
                    ))}
                    <UI.NavCard
                      to={r('Calendar')}
                      sc={{ placeholder: true, big: true, flex: { justifyContent: 'center', alignItems: 'center' } }}
                      title={t('website.view_all')}
                    >
                      <UI.Div>
                        <UI.FlexContainer sc={{ justifyContent: 'center', mb: 2 }}>
                          <UI.Icon sc={{ size: 2 }}>
                            <Calendar strokeWidth={1.125} />
                          </UI.Icon>
                        </UI.FlexContainer>
                        <UI.Div>
                          {t('website.view_all')}
                          {' '}
                          <UI.Icon>
                            <ArrowRight />
                          </UI.Icon>
                        </UI.Div>
                      </UI.Div>
                    </UI.NavCard>
                  </EventSlider>
                )}
              </>
            )}

            <UI.Div sc={{ textAlign: ['left', 'center'], big: true }}>
              <UI.Link
                to={`${r('Calendar')}?${encodeUrl({ countries: country ? [country] : null })}`}
                sc={{ secondary: true, fontWeight: 500 }}
              >
                {t('website.view_upcoming_events')}
                {' '}
                <UI.Icon>
                  <ChevronRight />
                </UI.Icon>
              </UI.Link>
            </UI.Div>
          </UI.GridContainer>

        </Container>
      </UI.Div>

      <OrganiserBanner>
        <Container size="lg">
          <UI.GridContainer sc={{ columns: ['1fr', '1fr 1fr 1fr'], alignVertical: 'center' }}>
            <UI.Div sc={{ px: [2, 0], pt: [6, 0] }} style={{ gridColumn: '1 / span 2' }}>
              <UI.Paragraph sc={{ color: 'secondary.300' }}>
                <UI.Label sc={{
                  outline: 'tertiary.700',
                  px: 1,
                  fontWeight: 500,
                  small: true,
                }}
                >
                  {t('website.organiser_banner.label')}
                </UI.Label>
              </UI.Paragraph>

              <UI.H1 sc={{ fontWeight: 700, mb: 2, color: 'white' }}>
                {t('website.organiser_banner.title')}
              </UI.H1>

              <UI.Paragraph
                sc={{ big: true, mb: 3, color: 'secondary.100' }}
                style={{ maxWidth: 700, textWrap: 'balance' }}
              >
                {t('website.organiser_banner.description')}
              </UI.Paragraph>

              <UI.UL
                sc={{
                  big: true, mb: 4, color: 'secondary.200', unstyled: true, spaceY: 1,
                }}
                style={{ maxWidth: 600 }}
              >
                <UI.LI sc={{ flex: {} }}>
                  <UI.Icon sc={{ color: 'secondary.400', mr: 2, mt: 2 / 3, mb: 0 }}>
                    <CheckCircle />
                  </UI.Icon>
                  <UI.Span>
                    {t('website.organiser_banner.usp_1')}
                  </UI.Span>
                </UI.LI>
                <UI.LI sc={{ flex: {} }}>
                  <UI.Icon sc={{ color: 'secondary.400', mr: 2, mt: 2 / 3, mb: 0 }}>
                    <CheckCircle />
                  </UI.Icon>
                  <UI.Span>
                    {t('website.organiser_banner.usp_2')}
                  </UI.Span>
                </UI.LI>
                <UI.LI sc={{ flex: {} }}>
                  <UI.Icon sc={{ color: 'secondary.400', mr: 2, mt: 2 / 3, mb: 0 }}>
                    <CheckCircle />
                  </UI.Icon>
                  <UI.Span>
                    {t('website.organiser_banner.usp_3')}
                  </UI.Span>
                </UI.LI>
              </UI.UL>

              <UI.FlexContainer sc={{ spaceX: 1 }}>
                <UI.AButton
                  href={r('Register', {}, { search: { locale } })}
                  sc={{ brand: 'secondary.600' }}
                >
                  {t('website.organiser_banner.create_event')}
                </UI.AButton>
                <UI.AButton
                  href={`https://about.atleta.cc/${locale}`}
                  sc={{ brand: 'secondary.600', outline: true, color: 'white' }}
                >
                  {t('website.organiser_banner.learn_more')}
                </UI.AButton>
              </UI.FlexContainer>
            </UI.Div>
          </UI.GridContainer>
        </Container>

        <img
          src={assetUrl('images/website/atleta-runner-home.svg')}
          alt="Atleta"
        />
      </OrganiserBanner>
    </UI.Div>
  );
};

const WelcomeGuestTitle = () => {
  const { t } = useTranslation();
  const { r } = useNavigation();

  const messages = useMemo(() => shuffle([
    'website.welcome_title_runner',
    'website.welcome_title_hiker',
    'website.welcome_title_adventurer',
    'website.welcome_title_nature_lover',
    'website.welcome_title_cycling_fanatic',
  ]), []);

  const icons = {
    'website.welcome_title_runner': SportType.running,
    'website.welcome_title_hiker': SportType.walking,
    'website.welcome_title_adventurer': SportType.running,
    'website.welcome_title_nature_lover': SportType.walking,
    'website.welcome_title_cycling_fanatic': SportType.cycling,
  };

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((index) => (index + 1) % messages.length);
    }, 4000);

    return () => window.clearInterval(timer);
  }, [messages]);

  const message = messages[index];

  return (
    <Container>
      <UI.FlexContainer sc={{ justifyContent: 'center' }}>
        <UI.Link to={r('Login')} title={t('log_in')}>
          <Avatar
            sportType={icons[message as keyof typeof icons] as SportType}
            background="secondary.700"
            key={message}
          />
        </UI.Link>
      </UI.FlexContainer>

      <UI.Div sc={{ textAlign: 'center' }}>
        <GuestTitle sc={{ fontSize: 4, color: 'white', mb: 1, noWrap: true }} key={message}>
          <Trans i18nKey={message}>
            <UI.Label sc={{ brand: 'secondary' }} />
          </Trans>
        </GuestTitle>
        <UI.Paragraph sc={{ color: 'secondary.100' }}>
          <Trans i18nKey="website.welcome_message_guest">
            <UI.Link to={r('Portal')} sc={{ strong: true }} />
          </Trans>
        </UI.Paragraph>
      </UI.Div>
    </Container>
  );
};

const GuestTitle = styled(UI.Paragraph)`
  animation: guest-fade 4s ease-in forwards;

  @keyframes guest-fade {
    0% {
      opacity: 0;
      transform: translateY(5px);
    }

    5% {
      opacity: 1;
      transform: translateY(0);
    }

    95% {
      opacity: 1;
      transform: translateY(0);
    }

    100% {
      opacity: 0;
      transform: translateY(-5px);
    }
  }
`;

const EventSlider = styled(UI.Div)`
  ${({ theme }) => css`
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    overflow-x: auto;
    display: flex;
    padding: ${theme.gutter * (2 / 3)}px;
    margin: -${theme.gutter * (2 / 3)}px;
    scroll-padding-left: ${theme.gutter * (2 / 3)}px;
    scroll-padding-right: ${theme.gutter * (2 / 3)}px;

    > * {
      flex: 0 0 95%;
      min-width: 320px;
      max-width: 464px;
      scroll-snap-align: start;
      scroll-snap-stop: always;
    }

    > * + * {
      margin-left: ${theme.gutter * 0.75}px;
    }

    @media ${theme.devices.md} {
      margin-left: -${theme.gutter * 2}px;
      margin-right: -${theme.gutter * 2}px;
      scroll-padding-left: ${theme.gutter * 2}px;
      scroll-padding-right: ${theme.gutter * 2}px;
      padding-left: ${theme.gutter * 2}px;
      padding-right: ${theme.gutter * 2}px;

      > * + * {
        margin-left: ${theme.gutter}px;
      }
    }

    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none;  /* IE 10+ */
    &::-webkit-scrollbar {
      display: none;
      width: 0px;
      height: 0px;
      background: transparent;
    }
  `}
`;

const OrganiserBanner = styled(UI.Div)`
  ${({ theme }) => css`
    background: radial-gradient(circle at bottom right, ${theme.colors.secondary[300]}, ${theme.colors.secondary[700]}, ${theme.colors.secondary[900]});
    position: relative;
    overflow: hidden;
    padding: 0 0 400px 0;

    @media ${theme.devices.md} {
      padding: ${theme.gutter * 2}px 0;
    }

    h1 {
      text-wrap: balance;
    }

    ${UI.Label} {
      background: linear-gradient(-60deg, ${theme.colors.primary[500]} 0%, ${theme.colors.tertiary[500]} 50%, ${theme.colors.secondary[300]} 100%);
      color: transparent;
      background-clip: text;
    }

    > ${Container} {
      position: relative;
      z-index: 2;
    }

    > img {
      position: absolute;
      top: 50%;
      left: -25%;
      width: 130vw;
      max-width: none !important;
      z-index: 1;

      @media ${theme.devices.md} {
        left: 50vw;
        top: -100px;
        width: 680px;
      }
    }
  `}
`;

interface RegistrationSummaryRowProps {
  summary: GetHomePage['session']['user']['registration_summaries'][0];
  muted?: boolean;
}

const RegistrationSummaryRow = ({ summary, muted = false }: RegistrationSummaryRowProps) => {
  const { r } = useNavigation();
  const { formatRelativeDate, formatDate, parseDate } = useLocale();

  const link = r('Registrations', {
    eventId: summary.event.id,
    projectId: summary.event.project.id,
    participantId: summary.participant.id,
    token: summary.view_token,
  });

  const hasStarted = isPast(parseDate(summary.event.start_date, { timezone: 'UTC' }));

  return (
    <NavItem
      to={link}
      title={summary.event.title}
      alignVertical="center"
      sc={{ muted }}
    >
      <UI.GridContainer sc={{ columns: '44px 1fr', gutter: 0.5 }}>
        <UI.ProfilePic
          sc={{ size: 44, background: summary.event.logo ? 'white' : undefined }}
        >
          {summary.event.logo && (
            <img
              src={resizedImageUrl(summary.event.logo.url, 300)}
              alt={summary.event.title}
            />
          )}
          {!summary.event.logo && (
            <UI.Span><UI.Span>{summary.event.title.charAt(0)}</UI.Span></UI.Span>
          )}
        </UI.ProfilePic>
        <UI.Div sc={{ pt: 0.5 }}>
          <UI.H4>
            {summary.event.title}
          </UI.H4>
          <UI.Delimit>
            {!hasStarted && ucFirst(formatRelativeDate(
              formatDate(
                parseDate(summary.event.start_date, { timezone: 'UTC' }),
                { format: 'internal_date_time' },
              ),
            ))}
            <DateRange
              start={summary.event.start_date}
              end={summary.event.end_date}
              timezone={summary.event.project.timezone}
            />
          </UI.Delimit>
        </UI.Div>
      </UI.GridContainer>
    </NavItem>
  );
};

const Header = styled(UI.Div)`
  ${({ theme }) => css`
    position: relative;
    overflow: hidden;
    margin-top: -${theme.scrollOffset.md}px;

    > div:last-child {
      padding-top: ${theme.scrollOffset.md}px;
      width: 100%;
      background: rgba(0, 0, 0, 0.3);
      background: linear-gradient(0deg, ${theme.colors.secondary[900]} 0%, ${theme.transparentize(theme.colors.secondary[900], 0.4)} 100%);
      position: relative;
      z-index: 3;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: space-between;

      > div:first-child {
        text-align: center;
        color: white;
        margin-top: auto;
        text-wrap: balance;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        position: relative;
        z-index: 3;

        h1 {
          font-size: 42px;
        }

        @media ${theme.devices.md} {
          h1 {
            font-size: 58px;
          }
        }

        ${UI.LinkButton} {
          box-shadow: 0 5px 25px ${theme.transparentize(theme.colors.primary[500], 0.5)}, inset 0 0 1px 1px ${theme.transparentize(theme.colors.primary[400], 0.7)};
        }
      }

       > svg {
        width: 100%;
        left: 0;
        right: 0;
        position: absolute;
        bottom: -45%;
        z-index: 2;
        opacity: 0.1;
        object-fit: cover;
      }

      > div:last-child {
        margin-top: auto;
        text-wrap: balance;
        position: relative;
        z-index: 3;
      }
    }
  `}
`;

const HeaderBackground = styled(UI.Div)`
  ${({ theme }) => css`
    background: ${theme.colors.secondary[900]};
    background-size: cover;
    position: absolute;
    inset: 0;
    z-index: 1;
    width: 100%;
    height: 100%;

    video {
      backdrop-filter: blur(10px);
      object-fit: cover;
      object-position: center center;
      width: 100%;
      height: 100%;
      display: block;
    }
  `}
`;

export default HomePage;
